import React, { FC, useCallback, useContext, useState } from 'react';

import { IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/CloseRounded';

import { GlobalContext } from '../../../../../store';
import {
  IMetaFormat,
  IMetaFormatBluePrint,
  TIFormatPrefix
} from '../../../../../store/admin/roundFormats/store';

import { Notify } from '../Utils/Notify';
import { OptionalFlightSection } from './OptionalFlightSection/OptionalFlightSection';
import { PlayersPerTeam } from './PlayersPerTeam';
import { SameFormatForAllRounds } from './SameFormatForAllRounds';
import { WhichScoresForEvent } from './WhichScoresForEvent';
import { HowManyStrikesBeforeEliminated } from './HowManyStrikesBeforeEliminated';
import { HowTeamsScore } from './HowTeamsScore';
import { TeamCardActionsStyled } from '../../../../../components/TeamCard/TeamCardActions/TeamCardActions.Styles';
import { RemoveModal } from '../../../../../components/TeamCard/RemoveTeamModal';
import { useBoolean } from '../../../../common/Hooks/useBoolean.hook';
import { useRouteInfoState } from '../../../../../components/RouteInfo/Context/RouteInfo.context';
import ScoringFormat from './ScoringFormat/ScoringFormat';

interface IProps {
  metaFormat: IMetaFormat;
}

export const GameContestFormatCard: FC<IProps> = props => {
  const { metaFormat } = props;

  const {
    actions: {
      adminEvents: { get: getEvent },
      adminRoundFormats: { deleteRoundFormat, updateRoundFormat },
      notifications: { push: pushNotification }
    }
  } = useContext(GlobalContext);

  const { clubId, eventId } = useRouteInfoState();

  const [metaFormatToUpdate, setMetaFormatToUpdate] = useState<IMetaFormat>(metaFormat);
  const [removeModalIsOpen, , toggleRemoveModalIsOpen] = useBoolean(false);

  const onDeleteFormats = (formatToDelete: any) => async () => {
    if (clubId && eventId) {
      const response = await deleteRoundFormat(Number(clubId), eventId, formatToDelete.id);

      await getEvent(clubId, eventId, true);

      await Notify(response, 'Format successfully deleted.', pushNotification);
    }
  };

  const onSaveMetaFormat = (metaFormatToSave: IMetaFormat) => {
    setMetaFormatToUpdate(metaFormatToSave);

    const formatBlueprint: IMetaFormatBluePrint = {
      ...metaFormatToSave,
      format: metaFormatToSave.format.id,
      metadata: metaFormatToSave.metadata ? metaFormatToSave.metadata : {}
    };

    const formatId = formatBlueprint.id;

    delete formatBlueprint.id;

    updateRoundFormat(clubId, eventId, formatId, formatBlueprint);
  };

  const isFormat = useCallback(
    (formatPrefix: TIFormatPrefix) => metaFormatToUpdate.format.prefix === formatPrefix,
    [metaFormatToUpdate]
  );

  const displayPlayersPerTeam =
    (isFormat('teamstroke') && metaFormatToUpdate.format.teams) ||
    isFormat('moneyBall') ||
    isFormat('moneyball') ||
    isFormat('scramble');

  return (
    <li className="selected-round-format" key={`selected-round-format-${metaFormat.id}`}>
      <div className="format-header">
        <span className="format-header__name name">{metaFormat.format.name}</span>

        <TeamCardActionsStyled>
          <IconButton
            color="primary"
            onClick={toggleRemoveModalIsOpen}
            aria-label="cancel"
            id="close-button"
          >
            <EditIcon />
          </IconButton>

          <RemoveModal
            modalIsOpen={removeModalIsOpen}
            toggleModalIsOpen={toggleRemoveModalIsOpen}
            onRemove={onDeleteFormats(metaFormat)}
            resourceName="Format"
          />
        </TeamCardActionsStyled>
      </div>

      <div className="format-body">
        <SameFormatForAllRounds
          metaFormatToUpdate={metaFormatToUpdate}
          onSaveMetaFormat={onSaveMetaFormat}
        />

        {displayPlayersPerTeam && (
          <PlayersPerTeam isMoneyBall={metaFormat.format.prefix.includes('money')} />
        )}

        <WhichScoresForEvent
          metaFormatToUpdate={metaFormatToUpdate}
          onSaveMetaFormat={onSaveMetaFormat}
        />

        {metaFormatToUpdate.format.optionalStableford && (
          <ScoringFormat
            metaFormatToUpdate={metaFormatToUpdate}
            onSaveMetaFormat={onSaveMetaFormat}
          />
        )}

        {isFormat('teamstroke') && (
          <HowTeamsScore
            metaFormatToUpdate={metaFormatToUpdate}
            onSaveMetaFormat={onSaveMetaFormat}
          />
        )}

        {isFormat('horse') && metaFormatToUpdate.format.optionalStrikes && (
          <HowManyStrikesBeforeEliminated
            metaFormatToUpdate={metaFormatToUpdate}
            onSaveMetaFormat={onSaveMetaFormat}
          />
        )}

        {metaFormatToUpdate.format.optionalFlight && (
          <OptionalFlightSection
            metaFormatToUpdate={metaFormatToUpdate}
            onSaveMetaFormat={onSaveMetaFormat}
          />
        )}
      </div>
    </li>
  );
};
